/**
 * GenzoX notifications helper (Iter49).
 *
 * Apple compatibility:
 *  - iOS Safari 16.4+ only supports notifications via ServiceWorkerRegistration.showNotification()
 *    AND only when the site is launched from Home Screen (standalone PWA mode).
 *  - macOS Safari and Chrome/Firefox/Edge desktop support both `new Notification()` and SW notifications.
 *
 * Strategy: prefer SW notification (works everywhere), fallback to `new Notification()` if no SW.
 */

export const isAppleDevice = () => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  // iPad on iOS 13+ identifies as Mac, so also check maxTouchPoints
  const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const isSafari = /Safari/.test(ua) && !/Chrome|CriOS|FxiOS|EdgiOS/.test(ua);
  return isIOS || isSafari;
};

export const isStandaloneIOS = () => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
  // iOS Safari sets navigator.standalone=true when launched from Home Screen
  return Boolean(navigator.standalone) || window.matchMedia?.('(display-mode: standalone)').matches;
};

/**
 * iOS Safari (non-PWA) lies about Notification.permission — it returns 'default' but requestPermission throws.
 * Use this to decide whether to even show the bell/enable-notifications button.
 */
export const notificationsSupported = () => {
  if (typeof Notification === 'undefined') return false;
  // On iOS Safari, the site MUST be in standalone (Home Screen) mode to receive notifications.
  const ua = navigator.userAgent || '';
  const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  if (isIOS && !isStandaloneIOS()) return false;
  return true;
};

export const requestNotificationPermission = async () => {
  if (typeof Notification === 'undefined') return 'unsupported';
  try {
    const perm = await Notification.requestPermission();
    return perm;
  } catch (e) {
    return 'denied';
  }
};

/**
 * Fire a notification. Prefers SW.showNotification() (iOS PWA compatible).
 * @param {string} title
 * @param {object} options - body, icon, tag, data (e.g. { url: '/chat/xxx' })
 */
export const showNotification = async (title, options = {}) => {
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return false;
  // Prefer service worker path — required on iOS 16.4+ PWA.
  if ('serviceWorker' in navigator) {
    try {
      const reg = await navigator.serviceWorker.ready;
      if (reg && typeof reg.showNotification === 'function') {
        await reg.showNotification(title, options);
        return true;
      }
    } catch (e) {
      // Fall through to direct Notification() below
    }
  }
  // Desktop fallback
  try {
    const n = new Notification(title, options);
    if (options.data?.url && typeof window !== 'undefined') {
      n.onclick = () => {
        window.focus();
        if (options.data.url !== window.location.pathname) {
          // Soft client-side nav if app exposes it via the global router, else hard nav
          window.location.href = options.data.url;
        }
        n.close();
      };
    }
    return true;
  } catch (e) {
    return false;
  }
};


// === Iter50: VAPID Push Subscription helpers ===

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const out = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) out[i] = rawData.charCodeAt(i);
  return out;
}

/**
 * Subscribe the current browser to VAPID push for the logged-in user.
 * Fetches the backend's VAPID public key, calls pushManager.subscribe, and POSTs the result.
 * @param {object} api - authenticated axios instance (e.g. the shared `api` from utils/api.js)
 * @returns {Promise<{ok: boolean, reason?: string}>}
 */
export const subscribeToVapidPush = async (api) => {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    return { ok: false, reason: 'unsupported' };
  }
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') {
    return { ok: false, reason: 'permission_not_granted' };
  }
  try {
    const reg = await navigator.serviceWorker.ready;
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      // Fetch public key and create a new subscription
      const keyResp = await api.get('/push/public-key');
      const publicKey = keyResp.data?.publicKey;
      if (!publicKey) return { ok: false, reason: 'no_vapid_key' };
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });
    }
    // Register with backend
    await api.post('/push/subscribe', sub.toJSON());
    return { ok: true };
  } catch (e) {
    return { ok: false, reason: e?.message || 'subscribe_failed' };
  }
};

export const unsubscribeFromVapidPush = async (api) => {
  if (!('serviceWorker' in navigator)) return { ok: false };
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (!sub) return { ok: true };
    const json = sub.toJSON();
    try { await api.post('/push/unsubscribe', json); } catch (_) {}
    await sub.unsubscribe();
    return { ok: true };
  } catch (e) {
    return { ok: false, reason: e?.message };
  }
};
